import {VIEW_TYPE, EDIT_TYPE, FACE_TYPE, AXIS_TYPE, OBJECT_TYPE, OBJECT_DTL_TYPE, PLANNING_TYPE, ATTRIBUTE_KEY} from '../constants/Type'
import * as FaceUtil from './Face'

/** 空間処理ユーティリティ */

/**
 * 空間データ探索
 * @param  {Number} spaceId 空間ID
 * @param {Object} 空間データ(ツリー)
 * @return {Object} 空間データ
 */
export const searchSpaceTree = (spaceId, space) => {
  if (spaceId === space.id) {
    return space
  }
  for (let child of space.childrenSpaceModel) {
    let targetSpace = searchSpaceTree(spaceId, child)
    if (targetSpace && Object.keys(targetSpace).length) {
      return targetSpace
    }
  }
  return {}
}

/**
 * 空間データツリーの再帰処理
 */
export const execRecursively = (space, func) => {
  func(space)
  if (!space.childrenSpaceModel || !space.childrenSpaceModel.length) return
  space.childrenSpaceModel.forEach(child => execRecursively(child, func))
}

/**
 * 親空間取得
 */
export const getParentByCond = (space, globalSpaces, filter) => {
  if (filter(space)) return space
  let parent = globalSpaces.find(s => s.id === space.parentSpaceId)
  if (!parent || !Object.keys(parent).length) return {}
  return getParentByCond(parent, globalSpaces, filter)
}

/**
 * 指定した条件に該当する最も一番上の親を取得する
 */
export const getTopParentByCond = (space, globalSpaces, filter) => {
  let topParent = {}
  let parent = space
  // 無限ループにならないよう空間レベルをカウント
  let spaceLevelCount = 0

  while (spaceLevelCount < space.spaceLevel) {
    parent = globalSpaces.find(s => s.id === parent.parentSpaceId)
    if (filter(parent)) {
      topParent = parent
    }
    if (parent == null || parent.parentSpaceId == null || parent.parentSpaceId === 0) {
      break
    }
    spaceLevelCount++
  }
  return topParent
}

/**
 * 画面表示用空間データ変換(3D->2D)
 * @param  {Object} data 変換元空間データ
 * @param  {Object} viewType ビュー種別(VIEW_TYPE)
 * @return {Object} 変換後空間データ
 */
export const convertSpaceDataForDisp = (data, viewType, viewRotation) => {
  if (!data || !Object.keys(data).length) {
    return data
  }
  let converted = Object.assign({}, data)
  switch (viewType) {
    case VIEW_TYPE.FRONT:
    case VIEW_TYPE.BACK:
      break
    case VIEW_TYPE.TOP:
    case VIEW_TYPE.BOTTOM:
      converted.y = data.z
      converted.z = data.y
      converted.h = data.d
      converted.d = data.h
      converted.minH = data.minD
      converted.maxH = data.maxD
      converted.minD = data.minH
      converted.maxD = data.maxH
      break
    case VIEW_TYPE.RIGHT:
    case VIEW_TYPE.LEFT:
      converted.x = data.z
      converted.z = data.x
      converted.w = data.d
      converted.d = data.w
      converted.minW = data.minD
      converted.maxW = data.maxD
      converted.minD = data.minW
      converted.maxD = data.maxW
      break
    default:
      break
  }
  if (viewRotation) {
    converted = rotateSpaceDataForDisp(converted, viewType, viewRotation)
  }
  return converted
}

/**
 * 画面表示用空間データ回転(2D)
 * @param  {Object} data 変換元2次元空間データ
 * @param  {Object} viewType ビュー種別(VIEW_TYPE)
 * @param  {Number} viewRotation ビューの回転角
 * @return {Object} 変換後空間データ
 */
export const rotateSpaceDataForDisp = (data, viewType, viewRotation) => {
  if (!Object.keys(data).length) {
    return data
  }
  // 直角のみ対応
  let isRotated = (!isNaN(viewRotation) && viewRotation % 180 !== 0)
  if (!isRotated) {
    return data
  }
  let converted = Object.assign({}, data)
  converted.x = data.y
  converted.y = data.x
  converted.w = data.h
  converted.h = data.w
  converted.minW = data.minH
  converted.maxW = data.maxH
  converted.minH = data.minW
  converted.maxH = data.maxW
  return converted
}

/**
 * 画面表示用空間データ変換(2D->3D)
 * @param  {Object} data 変換元空間データ
 * @param  {Object} viewType ビュー種別(VIEW_TYPE)
 * @param  {Number} viewRotation ビューの回転角
 * @return {Object} 変換後空間データ
 */
export const convertSpaceDataFromDisp = (data, viewType, viewRotation) => {
  if (!Object.keys(data).length) {
    return data
  }
  // 直角のみ対応
  let rotated = (!isNaN(viewRotation) && viewRotation % 180 !== 0)
  let converted = Object.assign({}, data)
  switch (viewType) {
    case VIEW_TYPE.FRONT:
    case VIEW_TYPE.BACK:
      break
    case VIEW_TYPE.TOP:
    case VIEW_TYPE.BOTTOM:
      if (rotated) {
        converted.z = data.x
        converted.x = data.y
        converted.y = data.z
        converted.d = data.w
        converted.w = data.h
        converted.h = data.d
        converted.minD = data.minW
        converted.maxD = data.maxW
        converted.minW = data.minH
        converted.maxW = data.maxH
        converted.minH = data.minD
        converted.maxH = data.maxD
      } else {
        converted.z = data.y
        converted.y = data.z
        converted.d = data.h
        converted.h = data.d
        converted.minD = data.minH
        converted.maxD = data.maxH
        converted.minH = data.minD
        converted.maxH = data.maxD
      }
      break
    case VIEW_TYPE.RIGHT:
    case VIEW_TYPE.LEFT:
      if (rotated) {
        converted.y = data.x
        converted.z = data.y
        converted.x = data.z
        converted.h = data.w
        converted.d = data.h
        converted.w = data.d
        converted.minH = data.minW
        converted.maxH = data.maxW
        converted.minD = data.minH
        converted.maxD = data.maxH
        converted.minW = data.minD
        converted.maxW = data.maxD
      } else {
        converted.z = data.x
        converted.x = data.z
        converted.d = data.w
        converted.w = data.d
        converted.minD = data.minW
        converted.maxD = data.maxW
        converted.minW = data.minD
        converted.maxW = data.maxD
      }
      break
    default:
      break
  }
  return converted
}

/**
 * 親空間原点加算
 * @param  {Object} data 変換元空間データ
 * @param  {Object} origin 原点座標
 * @return {Object} 変換後空間データ
 */
export const addParentOrigin = (data, origin) => {
  if (!Object.keys(data).length) {
    return data
  }
  var converted = Object.assign({}, data)
  converted.x += origin.x
  converted.y += origin.y
  converted.z += origin.z
  return converted
}

/**
 * 空間の軸種別から画面表示用軸種別への変換(3D->2D)
 * @param  {string} faceType 空間上の面種別
 * @param  {Object} viewType ビュー種別(VIEW_TYPE)
 * @param  {Number} viewRotation ビューの回転角
 * @return {string} 実際の面種別
 */
export const convertAxisTypeForDisp = (axisType, viewType, viewRotation) => {
  if (!axisType || !viewType) {
    return axisType
  }
  // ビューの回転を反映
  let converted = getAxisByViewRotation(axisType, viewType, viewRotation)
  switch (viewType) {
    case VIEW_TYPE.FRONT:
    case VIEW_TYPE.BACK:
      break
    case VIEW_TYPE.TOP:
    case VIEW_TYPE.BOTTOM:
      switch (converted) {
        case AXIS_TYPE.Z: converted = AXIS_TYPE.Y; break
        case AXIS_TYPE.Y: converted = AXIS_TYPE.Z; break
      }
      break
    case VIEW_TYPE.RIGHT:
    case VIEW_TYPE.LEFT:
      switch (converted) {
        case AXIS_TYPE.Z: converted = AXIS_TYPE.X; break
        case AXIS_TYPE.X: converted = AXIS_TYPE.Z; break
      }
      break
    default:
  }
  return converted
}

/**
 * ビューの回転を考慮して、空間の軸を取得する
 */
const getAxisByViewRotation = (axisType, viewType, viewRotation) => {
  let axisList = getAxisList(viewType)
  if (!axisList.includes(axisType)) {
    return axisType
  }
  // viewRotationが正の場合：順方向、負の場合：逆方向
  let rotateStep = isNaN(viewRotation) ? 0 : (2 + Math.floor((viewRotation % 180) / 90)) % 2
  let idx = (axisList.indexOf(axisType) + rotateStep) % 2
  return axisList[idx]
}

/**
 * ビューに応じた空間の軸リストを返却する
 */
const getAxisList = (viewType) => {
  let axisList = []
  switch (viewType) {
    case VIEW_TYPE.FRONT:
    case VIEW_TYPE.BACK:
      axisList = [AXIS_TYPE.X, AXIS_TYPE.Y]
      break
    case VIEW_TYPE.TOP:
    case VIEW_TYPE.BOTTOM:
      axisList = [AXIS_TYPE.X, AXIS_TYPE.Z]
      break
    case VIEW_TYPE.RIGHT:
    case VIEW_TYPE.LEFT:
      axisList = [AXIS_TYPE.Z, AXIS_TYPE.Y]
      break
  }
  return axisList
}

/**
 * 空間移動可否判定
 */
export const isMovable = (editCtrlSpaces, topSpace) => {
  // コンバイン編集時はスケルトン移動不可
  if (topSpace.spaceObjectType === OBJECT_TYPE.COMBINE.code &&
    editCtrlSpaces.find(space => space.planningType === PLANNING_TYPE.UNIT_SKELTON)) {
    return false
  }
  // 複数扉は移動不可
  let infills = editCtrlSpaces.filter(space => {
    return [ PLANNING_TYPE.UNIT_DOOR ].includes(space.planningType)
  })
  if (infills.length > 1) return false
  return true
}

/**
 * 空間リサイズ可否判定
 */
export const isResizable = (editCtrlSpaces, topSpace, editFace) => {
  // コンバイン編集時はスケルトンの基準面リサイズ不可
  if (topSpace.spaceObjectType === OBJECT_TYPE.COMBINE.code &&
    editCtrlSpaces.find(space => space.planningType === PLANNING_TYPE.UNIT_SKELTON) &&
    FaceUtil.isBaseFace(editFace)) {
    return false
  }
  // 棚、インフィルはリサイズ不可
  let unResizable = editCtrlSpaces.find(space => {
    return [PLANNING_TYPE.UNIT_SHELF, PLANNING_TYPE.UNIT_INFILL].includes(space.planningType)
  })
  if (unResizable) return false
  return true
}

/**
 * 空間移動
 * @param  {Object} data 空間データ
 * @param  {Object} amount 画面上での変化量
 * @param  {Object} viewType ビュー種別(VIEW_TYPE)
 * @return {Object} 計算後空間データ
 */
export const moveSpace = (data, amount, viewType, viewRotation) => {
  // 移動量をビューを考慮したデータに変換
  let convertedAmount = convertSpaceDataFromDisp({x: amount.dx, y: amount.dy}, viewType, viewRotation)

  // 計算後空間データ
  var calculated = Object.assign({}, data)

  // 移動
  calculated.x += isNaN(convertedAmount.x) ? 0 : convertedAmount.x
  calculated.y += isNaN(convertedAmount.y) ? 0 : convertedAmount.y
  calculated.z += isNaN(convertedAmount.z) ? 0 : convertedAmount.z

  return calculated
}

/**
 * 空間リサイズ
 * @param  {Object} data 空間データ
 * @param  {Object} amount 画面上での変化量
 * @param  {Object} viewType ビュー種別(VIEW_TYPE)
 * @param  {string} editFace 編集面種別(FACE_TYPE)
 * @return {Object} 計算後空間データ
 */
export const resizeSpace = (data, amount, viewType, editFace, viewRotation) => {
  // 移動量をビューを考慮したデータに変換
  let convertedAmount = convertSpaceDataFromDisp({x: amount.dx, y: amount.dy}, viewType, viewRotation)

  // 計算後空間データ
  var calculated = Object.assign({}, data)

  // リサイズ
  var maxW = 0.0
  var minW = 0.0
  var maxH = 0.0
  var minH = 0.0
  var maxD = 0.0
  var minD = 0.0
  switch (editFace) {
    // 右面
    case FACE_TYPE.RIGHT:
      calculated.w += isNaN(convertedAmount.x) ? 0 : convertedAmount.x
      maxW = data.limitMaxW == null ? data.maxW : data.limitMaxW
      minW = data.limitMinW == null ? data.minW : data.limitMinW
      if (maxW && calculated.w > maxW) {
        calculated.w = maxW
      }
      if (minW && calculated.w < minW) {
        calculated.w = minW
      }
      break
    // 左面
    case FACE_TYPE.LEFT:
      calculated.x += isNaN(convertedAmount.x) ? 0 : convertedAmount.x
      calculated.w -= isNaN(convertedAmount.x) ? 0 : convertedAmount.x
      maxW = data.limitMaxW == null ? data.maxW : data.limitMaxW
      minW = data.limitMinW == null ? data.minW : data.limitMinW
      if (maxW && calculated.w > maxW) {
        calculated.w = maxW
        calculated.x = data.x + data.w - maxW
      }
      if (minW && calculated.w < minW) {
        calculated.w = minW
        calculated.x = data.x + data.w - minW
      }
      break
    // 上面
    case FACE_TYPE.TOP:
      calculated.h += isNaN(convertedAmount.y) ? 0 : convertedAmount.y
      maxH = data.limitMaxH == null ? data.maxH : data.limitMaxH
      minH = data.limitMinH == null ? data.minH : data.limitMinH
      if (maxH && calculated.h > maxH) {
        calculated.h = maxH
      }
      if (minH && calculated.h < minH) {
        calculated.h = minH
      }
      break
    // 下面
    case FACE_TYPE.BOTTOM:
      calculated.y += isNaN(convertedAmount.y) ? 0 : convertedAmount.y
      calculated.h -= isNaN(convertedAmount.y) ? 0 : convertedAmount.y
      maxH = data.limitMaxH == null ? data.maxH : data.limitMaxH
      minH = data.limitMinH == null ? data.minH : data.limitMinH
      if (maxH && calculated.h > maxH) {
        calculated.h = maxH
        calculated.y = data.y + data.h - maxH
      }
      if (minH && calculated.h < minH) {
        calculated.h = minH
        calculated.y = data.y + data.h - minH
      }
      break
    // 前面
    case FACE_TYPE.FRONT:
      calculated.d += isNaN(convertedAmount.z) ? 0 : convertedAmount.z
      maxD = data.limitMaxD == null ? data.maxD : data.limitMaxD
      minD = data.limitMinD == null ? data.minD : data.limitMinD
      if (maxD && calculated.d > maxD) {
        calculated.d = maxD
      }
      if (minD && calculated.d < minD) {
        calculated.d = minD
      }
      break
    // 後面
    case FACE_TYPE.BACK:
      calculated.z += isNaN(convertedAmount.z) ? 0 : convertedAmount.z
      calculated.d -= isNaN(convertedAmount.z) ? 0 : convertedAmount.z
      maxD = data.limitMaxD == null ? data.maxD : data.limitMaxD
      minD = data.limitMinD == null ? data.minD : data.limitMinD
      if (maxD && calculated.d > maxD) {
        calculated.d = maxD
        calculated.z = data.z + data.d - maxD
      }
      if (minD && calculated.d < minD) {
        calculated.d = minD
        calculated.z = data.z + data.d - minD
      }
      break
    default:
  }

  return calculated
}

/**
 * 指定した空間の配下の全ての空間IDを配列に追加
 * @param space 指定した空間
 * @param spaceIds 空間IDの配列
 * @param globalSpaces グローバルスペースの配列
 * @param attributeModelList アトリビュート情報の配列
 */
export const createSklUnderSpaceIds = (space, spaceIds, globalSpaces, attributeModelList) => {
  space.childrenSpaceModel.forEach(childSpace => {
    if (childSpace.spaceObjectType === OBJECT_TYPE.CELL.code) {
      let cellAttr = attributeModelList.find(att => att.spaceId === childSpace.id &&
        att.attKeycd === ATTRIBUTE_KEY.SP_CELL_LINKED_DOOR)
      // 扉のアトリビュート情報を持っていた場合、扉も対象とする
      if (cellAttr != null) {
        let door = globalSpaces.find(globalSpace => globalSpace.id != null && globalSpace.id === cellAttr.attValue1)
        if (door != null) {
          spaceIds.push(door.id)
          if (door.childrenSpaceModel != null && door.childrenSpaceModel.length > 0) {
            createSklUnderSpaceIds(door, spaceIds, globalSpaces, attributeModelList)
          }
        }
      }
    }
    spaceIds.push(childSpace.id)
    if (childSpace.childrenSpaceModel != null && childSpace.childrenSpaceModel.length > 0) {
      createSklUnderSpaceIds(childSpace, spaceIds, globalSpaces, attributeModelList)
    }
  })
}

/**
 * ビュー毎のグローバル座標変換済み空間マップ生成
 * @return {Map} キー：ビュー種別、値：空間モデル配列
 */
export const createGlobalSpaceMap = (fileData, viewTypes, objectDataModelMap) => {
  const map = new Map()
  if (!fileData || !Object.keys(fileData).length) return map
  const { topSpaceModel } = fileData
  if (!topSpaceModel || !Object.keys(topSpaceModel).length) return map
  // 対象のビュー種別。viewTypes未設定の場合は全部
  const viewTypeList = (!viewTypes || !viewTypes.length)
    ? Object.keys(VIEW_TYPE).map(viewTypeName => VIEW_TYPE[viewTypeName])
    : viewTypes
  // 初期化
  viewTypeList.forEach(viewType => map.set(viewType, []))
  // 変換マップ作成
  convertToGlobalSpace(topSpaceModel, fileData, map, {x: 0, y: 0, z: 0}, viewTypeList, objectDataModelMap)
  // ソート
  viewTypeList.forEach(viewType => {
    map.get(viewType).sort((a, b) => {
      // 前後関係を最優先
      const diff = viewType.isBase ? (b.z - a.z) : ((a.z + a.d) - (b.z + b.d))
      if (diff !== 0) return diff
      // ユニット＞ユニットコンポーネント＞セル＞その他
      if (a.spaceObjectType === OBJECT_TYPE.UNIT.code) return 1
      if (b.spaceObjectType === OBJECT_TYPE.UNIT.code) return -1
      if (a.spaceObjectType === OBJECT_TYPE.UNITCOMPONENT.code) return 1
      if (b.spaceObjectType === OBJECT_TYPE.UNITCOMPONENT.code) return -1
      if (a.spaceObjectType === OBJECT_TYPE.CELL.code) return 1
      if (b.spaceObjectType === OBJECT_TYPE.CELL.code) return -1
    })
  })
  return map
}
/** ビュー毎のグローバル座標変換済み空間マップ生成(再帰処理用) */
function convertToGlobalSpace (space, fileData, map, parentOrigin, viewTypeList, objectDataModelMap) {
  if (!Object.keys(space).length) return
  // オブジェクトデータによるフィルタリング
  if (Object.keys(objectDataModelMap).length) {
    const objectData = objectDataModelMap[space.id]
    if (objectData && (objectData.objEnableFlag === 0 || objectData.objDrawFlag === 0)) return
  }
  // 親空間の座標を加算
  const convertedOrigin = addParentOrigin(space, parentOrigin)
  for (let viewType of viewTypeList) {
    // ビューの座標系に変換して追加
    const convertedForDisp = convertSpaceDataForDisp(convertedOrigin, viewType)
    map.get(viewType).push(convertedForDisp)
  }
  // 再帰的に呼び出し
  if (space.childrenSpaceModel) {
    for (let child of space.childrenSpaceModel) {
      convertToGlobalSpace(child, fileData, map, convertedOrigin, viewTypeList, objectDataModelMap)
    }
  }
}

/**
 * 操作対象空間取得
 * カーソル直下の空間を取得する。
 * 空間の種別や処理に応じた操作対象空間の判定は別途ラップした処理を用意する。
 * @param {Object} selfSpace 自空間(グローバル座標、ビュー変換前)
 * @param {Map} globalSpaceMap createGlobalSpaceMap()で作成したマップ
 * @param {Object} viewType ビュー種別(VIEW_TYPE)
 * @param zoomRatio svg拡大率
 * @param {Object} currentPosition 現在のポインタ位置
 * @param {Number} viewRotation ビューの回転角
 * @param filter フィルター
 * @returns {*[]|*|Array}
 */
export const getTargetSpaces = (selfSpace, globalSpaceMap, viewType, zoomRatio, currentPosition, viewRotation, filter = space => true) => {
  if (!globalSpaceMap || !viewType || !globalSpaceMap.get(viewType) || !currentPosition) {
    return []
  }

  // 操作してる空間が表面オプションの場合は空間の選択幅を広げて取得する
  let margin = PLANNING_TYPE.UNIT_FACE_OPTION === selfSpace.planningType ? zoomRatio * 10 : 0
  return globalSpaceMap.get(viewType)
    .map(space => rotateSpaceDataForDisp(space, viewType, viewRotation))
    .filter(filter)
    .filter(space =>
      space.x - margin <= currentPosition.x &&
      currentPosition.x <= space.x + space.w + margin &&
      space.y - margin <= currentPosition.y &&
      currentPosition.y <= space.y + space.h + margin
    )
}

/**
 * 操作対象空間取得
 * 空間の選択幅を広げて取得する。
 * 空間の種別や処理に応じた操作対象空間の判定は別途ラップした処理を用意する。
 * @param  {Map} globalSpaceMap createGlobalSpaceMap()で作成したマップ
 * @param  {Object} viewType ビュー種別(VIEW_TYPE)
 * @param  {Object} currentPosition 現在のポインタ位置
 * @param  {Number} viewRotation ビューの回転角
 * @param objectTypes オブジェクトタイプの配列
 * @param firstFilter フィルター
 * @param secondFilter フィルター
 * @returns {*[]|*|Array}
 */
export const getRangeTargetSpaces = (globalSpaceMap, viewType, zoomRatio, currentPosition, viewRotation, objectTypes, firstFilter = space => true, secondFilter = space => true) => {
  if (!globalSpaceMap || !viewType || !globalSpaceMap.get(viewType) || !currentPosition) {
    return []
  }
  let margin = zoomRatio * 10
  let tmpTargetSpaces = globalSpaceMap.get(viewType)
    .map(space => rotateSpaceDataForDisp(space, viewType, viewRotation))
    .filter(firstFilter)
    .filter(space =>
      // 空間の選択幅を広げている
      space.x - margin <= currentPosition.x &&
      currentPosition.x <= space.x + space.w + margin &&
      space.y - margin <= currentPosition.y &&
      currentPosition.y <= space.y + space.h + margin
    )
  return extractTargetSpaces(tmpTargetSpaces, globalSpaceMap, viewType, currentPosition, viewRotation, objectTypes, firstFilter, secondFilter)
}

/**
 * 操作対象空間抽出
 * 操作対象空間が1件の場合はそのまま返す。
 * 操作対象空間が2件以上ある場合は抽出する(操作対象空間の選択幅を広げており、意図しない空間が対象になっているため)。
 * @param tmpTargetSpaces 一時的な操作対象空間
 * @param {Map} globalSpaceMap createGlobalSpaceMap()で作成したマップ
 * @param {Object} viewType ビュー種別(VIEW_TYPE)
 * @param {Object} currentPosition 現在のポインタ位置
 * @param {Number} viewRotation ビューの回転角
 * @param objectTypes オブジェクトタイプの配列
 * @param {Object} firstFilter フィルター
 * @param {Object} secondFilter フィルター
 * @returns {*[]|*}
 */
export const extractTargetSpaces = (tmpTargetSpaces, globalSpaceMap, viewType, currentPosition, viewRotation, objectTypes, firstFilter, secondFilter) => {
  if (tmpTargetSpaces.length <= 1) {
    return tmpTargetSpaces
  }

  // 選択できる空間でフィルターをかけ、カーソルに近い順にソート
  let targetSpaces = tmpTargetSpaces
    .filter(secondFilter)
    .sort((a, b) => {
      let aMinX = Math.min(Math.abs(a.x - currentPosition.x), Math.abs(a.x + a.w - currentPosition.x))
      let aMinY = Math.min(Math.abs(a.y - currentPosition.y), Math.abs(a.y + a.h - currentPosition.y))
      let bMinX = Math.min(Math.abs(b.x - currentPosition.x), Math.abs(b.x + b.w - currentPosition.x))
      let bMinY = Math.min(Math.abs(b.y - currentPosition.y), Math.abs(b.y + b.h - currentPosition.y))
      return Math.min(aMinX, aMinY) - Math.min(bMinX, bMinY)
    })

  // 選択できる空間が無ければ空のリストを返す
  if (targetSpaces.length < 1) return []

  // カーソル直下の空間を取得
  let cursorUnderSpaces = globalSpaceMap.get(viewType)
    .map(space => rotateSpaceDataForDisp(space, viewType, viewRotation))
    .filter(firstFilter)
    .filter(space => space.x <= currentPosition.x &&
      currentPosition.x <= (space.x + space.w) &&
      space.y <= currentPosition.y &&
      currentPosition.y <= (space.y + space.h)
    )
    .filter(secondFilter)

  // カーソル直下のコンバインかスケルトンの空間を取得
  let cursorUnderCombineOrSkeleton = cursorUnderSpaces
    .filter(space => [PLANNING_TYPE.UNIT_SKELTON, PLANNING_TYPE.COMBINE].includes(space.planningType))

  // 「カーソル直下のコンバインかスケルトンの空間」がない場合
  if (cursorUnderCombineOrSkeleton.length < 1) {
    return targetSpaces.length > 1 &&
        (targetSpaces[0].planningType === PLANNING_TYPE.COMBINE || targetSpaces[1].planningType === PLANNING_TYPE.COMBINE)
      // カーソルに近い2つの空間のどちらかにコンバインがあれば、カーソルに近い2つの空間を返す
      ? [targetSpaces[0], targetSpaces[1]]
      // パーツ選択中はそのまますべて返す
      : objectTypes[0] === OBJECT_TYPE.PARTS.code ? targetSpaces
        // カーソルに最も近い空間を返す
        : [targetSpaces[0]]
  }

  // カーソル直下のコンバインかつスケルトン以外の空間を取得
  let cursorUnderNotCombineAndSkeleton = cursorUnderSpaces
    .filter(space => ![PLANNING_TYPE.COMBINE, PLANNING_TYPE.UNIT_SKELTON].includes(space.planningType))

  // 「カーソル直下のコンバインかつスケルトン以外の空間」がある場合は、「カーソル直下の空間」を返す
  if (cursorUnderNotCombineAndSkeleton.length > 0) {
    return cursorUnderSpaces
  }

  // 一時的な操作対象空間の中から「カーソル直下のコンバインかスケルトンの空間」配下の空間を取得する
  let underCombineOrSkeletonSpaces = targetSpaces
    .filter(space =>
      cursorUnderCombineOrSkeleton[0].x <= space.x && space.x <= cursorUnderCombineOrSkeleton[0].x + cursorUnderCombineOrSkeleton[0].w &&
      cursorUnderCombineOrSkeleton[0].y <= space.y && space.y <= cursorUnderCombineOrSkeleton[0].y + cursorUnderCombineOrSkeleton[0].h &&
      ![PLANNING_TYPE.COMBINE, PLANNING_TYPE.UNIT_SKELTON].includes(space.planningType)
    )

  if (underCombineOrSkeletonSpaces.length > 0) {
    // 一時的な操作対象空間の中に「カーソル直下のコンバインかスケルトンの空間」配下の空間がある場合、「カーソル直下のコンバインかスケルトンの空間」とカーソルに最も近い空間を返す
    cursorUnderCombineOrSkeleton.push(underCombineOrSkeletonSpaces[0])
  }

  return cursorUnderCombineOrSkeleton
}

/**
 * グローバル空間取得
 * @param  {Number} spaceId 空間ID
 * @param  {Map} globalSpaceMap createGlobalSpaceMap()で作成したマップ
 */
export const getGlobalSpace = (spaceId, globalSpaceMap) => {
  if (!spaceId || !globalSpaceMap || !globalSpaceMap.get(VIEW_TYPE.FRONT)) {
    return {}
  }
  let targetSpace = globalSpaceMap.get(VIEW_TYPE.FRONT).find(space => space.id === spaceId)
  return targetSpace
}

/**
 * 追加対象空間取得
 * 空間の種別に応じた追加対象空間の判定ロジックは当処理で吸収する。
 * 上書き対象の空間を取得する操作にも使用されている。
 * @param  {Object} selfSpace 自空間(グローバル座標、ビュー変換前)
 * @param  {Object} selfAttr 自空間のアトリビュート情報
 * @param  {Map} globalSpaceMap createGlobalSpaceMap()で作成したマップ
 * @param  {Object} viewType ビュー種別(VIEW_TYPE)
 * @param  zoomRatio svg拡大率
 * @param  {Object} currentPosition 現在のポインタ位置
 * @param  {string} fileData
 * @param  {Set} disabledSpaceIds 除外対象空間ID
 * @returns {Object} 追加対象空間(グローバル座標、ビュー変換後)
 */
export const getAddTargetSpace = (selfSpace, selfAttr, globalSpaceMap, viewType, zoomRatio, currentPosition, viewRotation, fileData, outOfSectionSpaceIds) => {
  if (!fileData || !fileData.topSpaceModel) {
    return {}
  }

  // プランニングタイプが未設定の空間は操作不可
  // カウンターは面指定で配置
  if (!selfSpace.planningType ||
    PLANNING_TYPE.UNDEFINED === selfSpace.planningType ||
    PLANNING_TYPE.UNIT_SURFACE === selfSpace.planningType) {
    return {}
  }

  let filterMethod = null

  // コンバイン編集時
  if (OBJECT_TYPE.COMBINE.code === fileData.topSpaceModel.spaceObjectType) {
    // スケルトン追加時
    if (PLANNING_TYPE.UNIT_SKELTON === selfSpace.planningType) {
      // コンバイン作成時で既にスケルトン配置済みの場合のスケルトン追加は無視
      if (!fileData.topSpaceModel.childrenSpaceModel.length) {
        filterMethod = space => OBJECT_TYPE.COMBINE.code === space.spaceObjectType
      }
    }

  // プランニング時
  } else if (OBJECT_TYPE.PLAN.code === fileData.topSpaceModel.spaceObjectType) {
    // ルームに追加できるのは、コンバインかスケルトンかアクセサリー
    if ([PLANNING_TYPE.UNIT_SKELTON, PLANNING_TYPE.ACCESSORY].includes(selfSpace.planningType)) {
      filterMethod = space => OBJECT_TYPE.ROOM.code === space.spaceObjectType
    }
    if (PLANNING_TYPE.COMBINE === selfSpace.planningType) {
      let targetSpaces = getTargetSpaces(selfSpace, globalSpaceMap, viewType, zoomRatio, currentPosition, viewRotation)
      let targetSpace = targetSpaces.find(s => s.planningType === PLANNING_TYPE.COMBINE)
      if (targetSpace != null) {
        filterMethod = space => OBJECT_TYPE.COMBINE.code === space.spaceObjectType
      } else {
        filterMethod = space => [OBJECT_TYPE.ROOM.code, OBJECT_TYPE.COMBINE.code].includes(space.spaceObjectType)
      }
    }
  }

  // ユニット追加時
  if (OBJECT_TYPE.UNIT.code === selfSpace.spaceObjectType) {
    // 表面オプション
    if (PLANNING_TYPE.UNIT_FACE_OPTION === selfSpace.planningType && selfAttr != null) {
      // 配置対象パーツが含まれるユニット
      let targetObjDtlType = selfAttr.attString5.split('|')[0]
      filterMethod = space => {
        if (OBJECT_TYPE.UNIT.code !== space.spaceObjectType) return false
        return space.childrenSpaceModel.filter(child => String(child.spaceObjectDtlType) === targetObjDtlType).length > 0
      }
    // スケルトンとアクセサリー以外
    } else if (![PLANNING_TYPE.UNIT_SKELTON, PLANNING_TYPE.ACCESSORY].includes(selfSpace.planningType)) {
      filterMethod = space => {
        let cond = OBJECT_TYPE.CELL.code === space.spaceObjectType &&
        space.childrenSpaceModel.filter(child => child.spaceObjectType === OBJECT_TYPE.CELL.code).length === 0
        // 固定棚の場合は引き出しを含むセルへは追加不可
        let drawerCell = OBJECT_DTL_TYPE.UNIT_FIX_SHELF.code === selfSpace.spaceObjectDtlType &&
        OBJECT_TYPE.CELL.code === space.spaceObjectType &&
        space.childrenSpaceModel.filter(child => child.spaceObjectDtlType === OBJECT_DTL_TYPE.UNIT_DRAWER.code).length > 0
        cond = cond && !drawerCell
        // 棚の場合は棚も追加対象（空間の上書き）
        cond = cond || (PLANNING_TYPE.UNIT_SHELF === selfSpace.planningType && PLANNING_TYPE.UNIT_SHELF === space.planningType)
        // 扉と引出しの場合は自分以外の連動扉空間を持たないセル
        if (PLANNING_TYPE.UNIT_DOOR === selfSpace.planningType || PLANNING_TYPE.UNIT_DRAWER === selfSpace.planningType) {
          let linkedDoorAtt = fileData.attributeModelList.find(att => {
            return att.attKeycd === ATTRIBUTE_KEY.SP_CELL_LINKED_DOOR &&
            att.spaceId === space.id &&
            att.attValue1 !== selfSpace.id
          })
          cond = cond && !linkedDoorAtt
          // 扉の場合は扉も追加対象（空間の上書き）
          cond = cond || (PLANNING_TYPE.UNIT_DOOR === selfSpace.planningType &&
            (PLANNING_TYPE.UNIT_DOOR === space.planningType && OBJECT_DTL_TYPE.UNIT_BACKPANEL.code !== space.spaceObjectDtlType))
          // 引き出しの場合は引き出しも追加対象（空間の上書き）
          cond = cond || (PLANNING_TYPE.UNIT_DRAWER === selfSpace.planningType && PLANNING_TYPE.UNIT_DRAWER === space.planningType)
        }
        // プランニング時コンバイン内へは追加不可
        if (OBJECT_TYPE.PLAN.code === fileData.topSpaceModel.spaceObjectType) {
          let parentCombine = getParentByCond(
            space,
            globalSpaceMap.get(viewType),
            comb => comb.spaceObjectType === OBJECT_TYPE.COMBINE.code
          )
          cond = cond && !(parentCombine && Object.keys(parentCombine).length)
        }
        return cond
      }
    }
  }

  // フィルター関数が設定されている場合のみ探索
  if (filterMethod) {
    let targetSpaces = getTargetSpaces(selfSpace, globalSpaceMap, viewType, zoomRatio, currentPosition, viewRotation)
    let excludeSpaceIds = outOfSectionSpaceIds.hasOwnProperty(viewType.code) ? outOfSectionSpaceIds[viewType.code] : new Set()
    let targetSpace = targetSpaces
      .filter(filterMethod)
      .filter(e => !excludeSpaceIds.has(e.id))
      .reduce((a, b) => {
        if (a.w > b.w || a.h > b.h) {
          return b
        }
        if (viewType.isBase) {
          return (a.z + a.d < b.z + b.d) ? a : b
        }
        return (a.z + a.d > b.z + b.d) ? a : b
      }, {}) // 最も小さいかつ手前もの

    // 上書きの場合、対象空間と自空間のサイズは考慮しない
    if (selfSpace.planningType === targetSpace.planningType) {
      return targetSpace
    }
    // 追加対象空間に自空間を配置できるスペースがあるかチェック
    // if (PLANNING_TYPE.UNIT_DRAWER === selfSpace.planningType) {
    //   // 追加対象空間(セル)と自空間(引き出し)が重ねて配置することができる追加対象空間(セル)の上下の空間の高さの合計値が、追加対象空間(引き出し)より高いかのチェック
    //   let globalSpaces = globalSpaceMap.get(VIEW_TYPE.FRONT)
    //   let drawerOverlapAbleSpaces = getDrawerOverlapAbleSpacesOfCell(targetSpace, globalSpaces)
    //   let topSpaceH = drawerOverlapAbleSpaces.top != null ? drawerOverlapAbleSpaces.top.h : 0
    //   let bottomSpaceH = drawerOverlapAbleSpaces.bottom != null ? drawerOverlapAbleSpaces.bottom.h : 0
    //   if (targetSpace.h + topSpaceH + bottomSpaceH < selfSpace.h) {
    //     return {}
    //   }
    // } else if (PLANNING_TYPE.UNIT_DOOR !== selfSpace.planningType) {
    //   // 自空間の高さが幅より大きい場合は幅が追加対象空間の幅より大きいかチェック。自空間の幅が高さより大きい場合は高さが追加対象空間の高さより大きいかチェック
    //   if ((selfSpace.h > selfSpace.w && targetSpace.w < selfSpace.w) || (selfSpace.h < selfSpace.w && targetSpace.h < selfSpace.h)) {
    //     return {}
    //   }
    // }
    return targetSpace
  }

  return {}
}

/**
 * 編集対象空間取得
 * @param  {Map} globalSpaceMap createGlobalSpaceMap()で作成したマップ
 * @param  {Object} viewType ビュー種別(VIEW_TYPE)
 * @param zoomRatio svg拡大率
 * @param  {Object} currentPosition 現在のポインタ位置
 * @param viewRotation ビューの回転角
 * @param outOfSectionSpaceIds 断面位置外空間IDリスト
 * @param objectTypes オブジェクトタイプの配列
 * @param planningTypes プランニングタイプの配列
 * @param filter フィルター
 * @returns {Object} 編集対象空間(グローバル座標、ビュー変換後)
 */

export const getEditTargetSpace = (globalSpaceMap, viewType, zoomRatio, currentPosition, viewRotation, outOfSectionSpaceIds, objectTypes = [], planningTypes = [], filter = space => true) => {
  let firstFilter = space => (!objectTypes.length || objectTypes.includes(space.spaceObjectType)) && (!planningTypes.length || planningTypes.includes(space.planningType))
  let secondFilter = null
  // ビュー種別によって選択できる空間
  switch (viewType) {
    case VIEW_TYPE.TOP:
    case VIEW_TYPE.BOTTOM:
      secondFilter = space => !(space.planningType === PLANNING_TYPE.UNIT_SHELF && space.spaceObjectDtlType !== OBJECT_DTL_TYPE.UNIT_INNER_SEPARATOR.code) &&
        !(space.planningType === PLANNING_TYPE.UNIT_INFILL && ![OBJECT_DTL_TYPE.UNIT_MOBILE_SEPARATOR.code, OBJECT_DTL_TYPE.UNIT_MOBILE_FRONT_BACK_SEPARATOR.code].includes(space.spaceObjectDtlType))
      break
    case VIEW_TYPE.RIGHT:
    case VIEW_TYPE.LEFT:
      secondFilter = space => !(space.planningType === PLANNING_TYPE.UNIT_SHELF && space.spaceObjectDtlType === OBJECT_DTL_TYPE.UNIT_INNER_SEPARATOR.code) &&
        !(space.planningType === PLANNING_TYPE.UNIT_INFILL && [OBJECT_DTL_TYPE.UNIT_MOBILE_SEPARATOR.code, OBJECT_DTL_TYPE.UNIT_MOBILE_FRONT_BACK_SEPARATOR.code].includes(space.spaceObjectDtlType))
      break
    default:
      secondFilter = () => true
      break
  }
  let targetSpaces = getRangeTargetSpaces(globalSpaceMap, viewType, zoomRatio, currentPosition, viewRotation, objectTypes, firstFilter, secondFilter)
  let excludeSpaceIds = outOfSectionSpaceIds.hasOwnProperty(viewType.code) ? outOfSectionSpaceIds[viewType.code] : new Set()
  // 対象空間が複数ある場合は、プラン空間は除外
  if (targetSpaces.length > 1) {
    targetSpaces = targetSpaces.filter(e => e.spaceObjectType !== OBJECT_TYPE.PLAN.code)
  }
  // パーツ選択時
  if (objectTypes[0] === OBJECT_TYPE.PARTS.code) {
    let faceType = FaceUtil.getFrontFace(viewType)
    return targetSpaces
      .filter(e => !excludeSpaceIds.has(e.id)) // 断面線外を除外
      .filter(filter)
      .reduce((a, b) => {
        if (viewType.isBase) {
          return FaceUtil.getFacePosition(a, faceType) < FaceUtil.getFacePosition(b, faceType) ? a : b
        }
        return FaceUtil.getFacePosition(a, faceType) > FaceUtil.getFacePosition(b, faceType) ? a : b
      }, {}) // 面座標が手前にあるもの
  }
  return targetSpaces
    .filter(e => !excludeSpaceIds.has(e.id)) // 断面線外を除外
    .filter(filter)
    .reduce((a, b) => {
      if (a.w < b.w && a.h < b.h) {
        if (viewType.isBase) return (a.z + a.d < b.z) ? a : b
        return (a.z + a.d > b.z) ? a : b
      }
      if (a.w > b.w && a.h > b.h) {
        if (viewType.isBase) return (b.z + b.d < a.z) ? b : a
        return (b.z + b.d > a.z) ? b : a
      }
      if (viewType.isBase) return (a.z + a.d < b.z + b.d) ? a : b
      return (a.z + a.d > b.z + b.d) ? a : b
    }, {}) // 最も小さいかつ手前もの
}

/**
 * 編集対象空間から追加対象空間を取得する
 * @param fileData ファイルデータ
 * @param editCtrlSpaces 編集対象空間(グローバル空間)
 * @param globalSpaceMap グローバル空間マップ
 * @returns {{}|*}
 */
export const getAddTargetSpaceByEditTargetSpace = (fileData, editCtrlSpaces, globalSpaceMap) => {
  if (editCtrlSpaces.length === 1 && editCtrlSpaces[0].planningType === PLANNING_TYPE.UNIT_DRAWER) {
    let editCtrlSpace = editCtrlSpaces[0]
    let nextCells = getNextCellsOfDrawer(fileData, editCtrlSpace, globalSpaceMap)
    let nextCellArr = Object.keys(nextCells).map(faceType => nextCells[faceType]).filter(space => space !== null)

    let addTargetSpaces = nextCellArr.filter(space =>
      // 引き出しの親のセルは除く
      editCtrlSpace.parentSpaceId &&
      space.x <= editCtrlSpace.x + editCtrlSpace.w / 2 && editCtrlSpace.x + editCtrlSpace.w / 2 <= space.x + space.w &&
      space.y <= editCtrlSpace.y + editCtrlSpace.h / 2 && editCtrlSpace.y + editCtrlSpace.h / 2 <= space.y + space.h
    )

    if (addTargetSpaces.length === 1) {
      return addTargetSpaces[0]
    }
  }

  return {}
}

/** 選択範囲に存在するユニット空間を返却 */
export const selectUnitSpaces = (selectArea, globalSpaceMap, viewType, viewRotation, filter = space => true) => {
  if (!globalSpaceMap || !Object.keys(selectArea).length || !viewType || !globalSpaceMap.get(viewType)) {
    return []
  }
  let minX = Math.min(selectArea.x1, selectArea.x2)
  let maxX = Math.max(selectArea.x1, selectArea.x2)
  let minY = Math.min(selectArea.y1, selectArea.y2)
  let maxY = Math.max(selectArea.y1, selectArea.y2)
  let selectedUnitSpaces = globalSpaceMap.get(viewType)
    .filter(e => OBJECT_TYPE.UNIT.code === e.spaceObjectType)
    .filter(filter)
    .map(e => rotateSpaceDataForDisp(e, viewType, viewRotation))
    .filter(e => {
      if (maxX < e.x || (e.x + e.w) < minX || maxY < e.y || (e.y + e.h) < minY) {
        return false
      }
      return true
    })
  return selectedUnitSpaces
}

/**
 * ポインタが対象空間上に乗っているか判定
 * @param  {Object} globalSpace グローバル空間
 * @param  {Object} viewType ビュー種別(VIEW_TYPE)
 * @param  {Object} currentPosition 現在のポインタ位置
 */
export const isOnSpace = (globalSpace, viewType, currentPosition, viewRotation, selectRange) => {
  if (!globalSpace || !Object.keys(globalSpace).length || !viewType || !currentPosition) {
    return false
  }
  let converted = convertSpaceDataForDisp(globalSpace, viewType, viewRotation)
  if (converted.x - selectRange <= currentPosition.x &&
    (converted.x + converted.w + selectRange) >= currentPosition.x &&
    converted.y - selectRange <= currentPosition.y &&
    (converted.y + converted.h + selectRange) >= currentPosition.y) {
    return true
  }
  return false
}

/**
 * ポインタが対象空間上の外周に乗っているか判定し、対象の面種別(2D)を返却する。
 * @param  {Object} globalSpace グローバル空間
 * @param  {Object} viewType ビュー種別(VIEW_TYPE)
 * @param  {Object} currentPosition 現在のポインタ位置
 */
export const isOnBorder = (globalSpace, viewType, currentPosition, viewRotation, viewInverse, selectRange) => {
  if (!globalSpace || !Object.keys(globalSpace).length || !viewType || !currentPosition ||
    !isOnSpace(globalSpace, viewType, currentPosition, viewRotation, selectRange)) {
    return FACE_TYPE.UNDEFINED
  }
  let converted = convertSpaceDataForDisp(globalSpace, viewType, viewRotation)
  if (Math.abs(currentPosition.x - converted.x) < selectRange) {
    return viewInverse.x ? FACE_TYPE.RIGHT : FACE_TYPE.LEFT
  }
  if (Math.abs(converted.x + converted.w - currentPosition.x) < selectRange) {
    return viewInverse.x ? FACE_TYPE.LEFT : FACE_TYPE.RIGHT
  }
  if (Math.abs(currentPosition.y - converted.y) < selectRange) {
    return viewInverse.y ? FACE_TYPE.TOP : FACE_TYPE.BOTTOM
  }
  if (Math.abs(converted.y + converted.h - currentPosition.y) < selectRange) {
    return viewInverse.y ? FACE_TYPE.BOTTOM : FACE_TYPE.TOP
  }
  return FACE_TYPE.UNDEFINED
}

/**
 * 固定空間判定
 * @param {Object} space 空間
 * @param {string} faceType 画面上の面種別
 * @return {boolean}
 */
export const isFix = (space, faceType) => {
  switch (faceType) {
    case FACE_TYPE.LEFT:
    case FACE_TYPE.RIGHT:
      return space.w === space.minW && space.w === space.maxW
    case FACE_TYPE.BOTTOM:
    case FACE_TYPE.TOP:
      return space.h === space.minH && space.h === space.maxH
    case FACE_TYPE.BACK:
    case FACE_TYPE.FRONT:
      return space.d === space.minD && space.d === space.maxD
    default:
  }
  return false
}

/**
 * 種別に応じた空間編集結果の補正を行う
 * @return {Object} 補正後の空間データ
 */
export const checkEditable = (space, fileData, editType, editFace, editCtrlableAreaMap, globalSpaceMap) => {
  let modSpace = Object.assign({}, space)
  let rtn = {
    modified: false,
    spaceData: modSpace
  }
  let editCtrlableArea = editCtrlableAreaMap[space.id]
  if (!editCtrlableArea || !Object.keys(editCtrlableArea).length) return rtn
  if (OBJECT_TYPE.COMBINE.code === fileData.topSpaceModel.spaceObjectType &&
    PLANNING_TYPE.UNIT_SKELTON === space.planningType) {
    // コンバイン編集時のトップスケルトンは移動不可
    modSpace = Object.assign(modSpace, { x: 0, y: 0, z: 0 })
  } else {
    // 引き出しが重ねて配置することができる空間
    let globalSpaces = globalSpaceMap.get(VIEW_TYPE.FRONT)
    let overlapAbleSpaces = getDrawerOverlapAbleSpacesOfCell(editCtrlableArea, globalSpaces)
    let leftSpaceW = overlapAbleSpaces.left != null ? overlapAbleSpaces.left.w : 0
    let rightSpaceW = overlapAbleSpaces.right != null ? overlapAbleSpaces.right.w : 0
    let bottomSpaceH = overlapAbleSpaces.bottom != null ? overlapAbleSpaces.bottom.h : 0
    let topSpaceH = overlapAbleSpaces.top != null ? overlapAbleSpaces.top.h : 0

    // 制約空間との整合性チェック
    switch (editType) {
      case EDIT_TYPE.MOVE:
        // 引き出し
        if (space.planningType === PLANNING_TYPE.UNIT_DRAWER) {
          if (space.x < editCtrlableArea.x - leftSpaceW) {
            modSpace.x = editCtrlableArea.x - leftSpaceW
            rtn.modified = true
          } else if ((editCtrlableArea.x + editCtrlableArea.w + rightSpaceW) < (space.x + space.w)) {
            modSpace.x = editCtrlableArea.x - leftSpaceW
            rtn.modified = true
          }
          if (space.y < editCtrlableArea.y - bottomSpaceH) {
            modSpace.y = editCtrlableArea.y - bottomSpaceH
            rtn.modified = true
          } else if ((editCtrlableArea.y + editCtrlableArea.h + topSpaceH) < (space.y + space.h)) {
            modSpace.y = editCtrlableArea.y + editCtrlableArea.h + topSpaceH - space.h
            rtn.modified = true
          }
        // その他の空間
        } else {
          if (space.x < editCtrlableArea.x) {
            modSpace.x = editCtrlableArea.x
            rtn.modified = true
          } else if ((editCtrlableArea.x + editCtrlableArea.w) < (space.x + space.w)) {
            modSpace.x = editCtrlableArea.x + editCtrlableArea.w - space.w
            rtn.modified = true
          }
          if (space.y < editCtrlableArea.y) {
            modSpace.y = editCtrlableArea.y
            rtn.modified = true
          } else if ((editCtrlableArea.y + editCtrlableArea.h) < (space.y + space.h)) {
            modSpace.y = editCtrlableArea.y + editCtrlableArea.h - space.h
            rtn.modified = true
          }
        }
        if (space.z < editCtrlableArea.z) {
          modSpace.z = editCtrlableArea.z
          rtn.modified = true
        } else if ((editCtrlableArea.z + editCtrlableArea.d) < (space.z + space.d)) {
          modSpace.z = editCtrlableArea.z + editCtrlableArea.d - space.d
          rtn.modified = true
        }
        break
      case EDIT_TYPE.RESIZE:
        switch (editFace) {
          case FACE_TYPE.LEFT:
            if (space.x < editCtrlableArea.x) {
              modSpace.x = editCtrlableArea.x
              modSpace.w = space.x + space.w - editCtrlableArea.x
              rtn.modified = true
            }
            break
          case FACE_TYPE.RIGHT:
            if (editCtrlableArea.x + editCtrlableArea.w < space.x + space.w) {
              modSpace.w = editCtrlableArea.x + editCtrlableArea.w - space.x
              rtn.modified = true
            }
            break
          case FACE_TYPE.BOTTOM:
            // 引き出し
            if (space.planningType === PLANNING_TYPE.UNIT_DRAWER) {
              if (space.y < editCtrlableArea.y - bottomSpaceH) {
                modSpace.y = editCtrlableArea.y - bottomSpaceH
                modSpace.h = space.y + space.h - editCtrlableArea.y
                rtn.modified = true
              }
            // その他の空間
            } else {
              if (space.y < editCtrlableArea.y) {
                modSpace.y = editCtrlableArea.y
                modSpace.h = space.y + space.h - editCtrlableArea.y
                rtn.modified = true
              }
            }
            break
          case FACE_TYPE.TOP:
            // 引き出し
            if (space.planningType === PLANNING_TYPE.UNIT_DRAWER) {
              if (editCtrlableArea.y + editCtrlableArea.h + topSpaceH < space.y + space.h) {
                modSpace.h = editCtrlableArea.y + editCtrlableArea.h + topSpaceH - space.y
                rtn.modified = true
              }
            // その他の空間
            } else {
              if (editCtrlableArea.y + editCtrlableArea.h < space.y + space.h) {
                modSpace.h = editCtrlableArea.y + editCtrlableArea.h - space.y
                rtn.modified = true
              }
            }
            break
          case FACE_TYPE.BACK:
            if (space.z < editCtrlableArea.z) {
              modSpace.z = editCtrlableArea.z
              modSpace.d = space.z + space.d - editCtrlableArea.z
              rtn.modified = true
            }
            break
          case FACE_TYPE.FRONT:
            if (editCtrlableArea.z + editCtrlableArea.d < space.z + space.d) {
              modSpace.d = editCtrlableArea.z + editCtrlableArea.d - space.z
              rtn.modified = true
            }
            break
        }
        break
    }
  }
  return rtn
}

/**
 * ビューに合わせて対象面を変換する
 * @param faceType 対象面
 * @param viewType ビュー
 * @returns {string|*}
 */
export const convertFaceTypeFromViewType = (faceType, viewType) => {
  switch (viewType) {
    case VIEW_TYPE.TOP:
      if (faceType === FACE_TYPE.TOP) return FACE_TYPE.BACK
      if (faceType === FACE_TYPE.BOTTOM) return FACE_TYPE.FRONT
      break
    case VIEW_TYPE.BOTTOM:
      if (faceType === FACE_TYPE.TOP) return FACE_TYPE.FRONT
      if (faceType === FACE_TYPE.BOTTOM) return FACE_TYPE.BACK
      break
    case VIEW_TYPE.RIGHT:
      if (faceType === FACE_TYPE.LEFT) return FACE_TYPE.FRONT
      if (faceType === FACE_TYPE.RIGHT) return FACE_TYPE.BACK
      break
    case VIEW_TYPE.LEFT:
      if (faceType === FACE_TYPE.LEFT) return FACE_TYPE.BACK
      if (faceType === FACE_TYPE.RIGHT) return FACE_TYPE.FRONT
      break
    case VIEW_TYPE.FRONT:
      break
    case VIEW_TYPE.BACK:
      if (faceType === FACE_TYPE.TOP) return FACE_TYPE.FRONT
      if (faceType === FACE_TYPE.BOTTOM) return FACE_TYPE.BACK
      break
    default:
      break
  }
  return faceType
}

/**
 * 指定点を中心として空間データの位置を設定
 * @param {Object} space 空間
 * @param {Object} point 中心点(2D)
 * @param {Object} viewType ビュー種別(VIEW_TYPE)
 * @param {Object} viewRotation ビュー回転
 */
export const setSpaceByCenter = (space, point, viewType, viewRotation) => {
  let convertedCenter = convertSpaceDataFromDisp(point, viewType, viewRotation)
  space.x = convertedCenter.x ? convertedCenter.x - space.w / 2 : space.x
  space.y = convertedCenter.y ? convertedCenter.y - space.h / 2 : space.y
  space.z = convertedCenter.z ? convertedCenter.z - space.d / 2 : space.z
  return space
}

/**
 * 棚板に隣接するセル空間(グローバル座標)を取得する
 */
export const getNextCellsOfShelf = (fileData, space, globalSpaceMap) => {
  let parent = searchSpaceTree(space.parentSpaceId, fileData.topSpaceModel)
  let brothers = parent.childrenSpaceModel
  let alignAtt = fileData.attributeModelList.find(att => {
    return ATTRIBUTE_KEY.UT_FLAG_ALIGN_DIR === att.attKeycd && att.spaceId === space.id
  })
  let cells = { left: null, right: null, bottom: null, top: null, back: null, front: null }
  let shelfIdx = 0
  let globalSpaces = globalSpaceMap.get(VIEW_TYPE.FRONT)
  switch (alignAtt.attString1) {
    case AXIS_TYPE.X:
      // ソート
      brothers.sort((a, b) => (a.x + a.w / 2) - (b.x + b.w / 2))
      for (let i = 0; i < brothers.length; i++) {
        if (brothers[i].id === space.id) {
          shelfIdx = i
          break
        }
      }
      // 左セル
      cells.left = globalSpaces.find(s => s.id === brothers[shelfIdx - 1].id)
      // 右セル
      cells.right = globalSpaces.find(s => s.id === brothers[shelfIdx + 1].id)
      break
    case AXIS_TYPE.Y:
      // ソート
      brothers.sort((a, b) => (a.y + a.h / 2) - (b.y + b.h / 2))
      for (let i = 0; i < brothers.length; i++) {
        if (brothers[i].id === space.id) {
          shelfIdx = i
          break
        }
      }
      // 下セル
      cells.bottom = globalSpaces.find(s => s.id === brothers[shelfIdx - 1].id)
      // 上セル
      cells.top = globalSpaces.find(s => s.id === brothers[shelfIdx + 1].id)
      break
    case AXIS_TYPE.Z:
      // ソート
      brothers.sort((a, b) => (a.z + a.d / 2) - (b.z + b.d / 2))
      for (let i = 0; i < brothers.length; i++) {
        if (brothers[i].id === space.id) {
          shelfIdx = i
          break
        }
      }
      // 後ろセル
      cells.back = globalSpaces.find(s => s.id === brothers[shelfIdx - 1].id)
      // 前セル
      cells.front = globalSpaces.find(s => s.id === brothers[shelfIdx + 1].id)
      break
    default:
  }
  return cells
}

/**
 * 引き出しの親のセルと上下に隣接するセルを取得する
 * @param fileData ファイルデータ
 * @param drawer 引き出し
 * @param globalSpaceMap グローバル空間マップ
 * @returns {{top: null, left: null, bottom: null, right: null}}
 */
export const getNextCellsOfDrawer = (fileData, drawer, globalSpaceMap) => {
  let cells = { left: null, right: null, bottom: null, top: null }
  let globalSpaces = globalSpaceMap.get(VIEW_TYPE.FRONT)
  let parent = searchSpaceTree(drawer.parentSpaceId, fileData.topSpaceModel)
  let parentParent = searchSpaceTree(parent.parentSpaceId, fileData.topSpaceModel)
  let brothers = parentParent.childrenSpaceModel
  let shelfIdx = 0

  // ソート
  brothers.sort((a, b) => (a.y + a.h / 2) - (b.y + b.h / 2))
  for (let i = 0; i < brothers.length; i++) {
    if (brothers[i].id === parent.id) {
      shelfIdx = i
      break
    }
  }

  // 引き出しの親のセル
  cells.self = globalSpaces.find(space => space.id === drawer.parentSpaceId)
  // 上セル
  if (brothers[shelfIdx + 2] != null) {
    cells.top = Object.assign(globalSpaces.find(s => s.id === brothers[shelfIdx + 2].id))
  }
  // 下セル
  if (brothers[shelfIdx - 2] != null) {
    cells.bottom = Object.assign(globalSpaces.find(s => s.id === brothers[shelfIdx - 2].id))
  }

  return cells
}

/**
 * 複数空間を包括する空間(グローバル座標)を返す
 */
export const getWrapSpace = (globalSpaces) => {
  let x = Math.min(...globalSpaces.map(e => e.x))
  let y = Math.min(...globalSpaces.map(e => e.y))
  let z = Math.min(...globalSpaces.map(e => e.z))
  let w = Math.max(...globalSpaces.map(e => e.x + e.w)) - x
  let h = Math.max(...globalSpaces.map(e => e.y + e.h)) - y
  let d = Math.max(...globalSpaces.map(e => e.z + e.d)) - z
  let wrapSpace = Object.assign({}, { x, y, z, w, h, d })
  return wrapSpace
}

/**
 * 追加操作用空間データの配置予定の空間を返す
 * 表示用に変換したデータを返す
 * @param addCtrlSpace ドラッグしてる空間（正面からの空間）
 * @param addTargetSpace 追加対象の空間
 * @param globalSpaceMap グローバル空間マップ
 * @param viewSet ビューセット
 * @returns {{}|{w: number, x: number, h: number, y: number}}
 */
export const getLocateAddCtrlSpace = (addCtrlSpace, addTargetSpace, globalSpaceMap, viewSet) => {
  if (![PLANNING_TYPE.UNIT_DRAWER, PLANNING_TYPE.UNIT_DOOR, PLANNING_TYPE.UNIT_SHELF, PLANNING_TYPE.UNIT_INFILL]
    .includes(addCtrlSpace.planningType)) {
    return convertSpaceDataForDisp(addCtrlSpace, viewSet.viewType, viewSet.rotate)
  }
  if (!addCtrlSpace || !addTargetSpace || !viewSet || !viewSet.viewType || addTargetSpace.spaceObjectType !== OBJECT_TYPE.CELL.code ||
    !globalSpaceMap || !globalSpaceMap.get(viewSet.viewType)) {
    return {}
  }
  // 追加予定の空間
  let locateAddCtrlSpace = {x: 0, y: 0, w: 0, h: 0, planningType: addCtrlSpace.planningType}
  // 追加対象のセル
  let addTargetCell = globalSpaceMap.get(viewSet.viewType).find(space => space.id === addTargetSpace.id)

  switch (addCtrlSpace.planningType) {
    case PLANNING_TYPE.UNIT_DOOR:
      if ([VIEW_TYPE.FRONT, VIEW_TYPE.BACK].includes(viewSet.viewType)) {
        locateAddCtrlSpace = {
          x: addTargetCell.x,
          y: addTargetCell.y,
          w: addTargetCell.w,
          h: addTargetCell.h
        }
      } else if ([VIEW_TYPE.RIGHT, VIEW_TYPE.LEFT].includes(viewSet.viewType)) {
        locateAddCtrlSpace = {
          x: addTargetCell.x + addTargetCell.w,
          y: addTargetCell.y,
          w: addCtrlSpace.d,
          h: addTargetCell.h
        }
      } else if ([VIEW_TYPE.TOP, VIEW_TYPE.BOTTOM].includes(viewSet.viewType)) {
        locateAddCtrlSpace = {
          x: addTargetCell.x,
          y: addTargetCell.y + addTargetCell.h,
          w: addTargetCell.w,
          h: addCtrlSpace.d
        }
      }
      break
    case PLANNING_TYPE.UNIT_DRAWER:
      if ([VIEW_TYPE.FRONT, VIEW_TYPE.BACK, VIEW_TYPE.RIGHT, VIEW_TYPE.LEFT].includes(viewSet.viewType)) {
        // 引き出しが重ねて配置することができるセルの上下の空間を取得
        let drawerOverlapAbleSpaces = getDrawerOverlapAbleSpacesOfCell(addTargetCell, globalSpaceMap.get(viewSet.viewType))
        // console.log(JSON.stringify(drawerOverlapAbleSpaces))
        // セルからはみ出ていたらセル内に収まるように設定する
        if (drawerOverlapAbleSpaces.bottom == null && addCtrlSpace.y < addTargetCell.y) {
          locateAddCtrlSpace.y = addTargetCell.y
        } else if (drawerOverlapAbleSpaces.bottom != null && addCtrlSpace.y < drawerOverlapAbleSpaces.bottom.y) {
          locateAddCtrlSpace.y = drawerOverlapAbleSpaces.bottom.y
        } else if (drawerOverlapAbleSpaces.top == null && addCtrlSpace.y + addCtrlSpace.h > addTargetCell.y + addTargetCell.h) {
          locateAddCtrlSpace.y = addTargetCell.y + addTargetCell.h - addCtrlSpace.h
        } else if (drawerOverlapAbleSpaces.top != null && addCtrlSpace.y + addCtrlSpace.h > drawerOverlapAbleSpaces.top.y + drawerOverlapAbleSpaces.top.h) {
          locateAddCtrlSpace.y = drawerOverlapAbleSpaces.top.y + drawerOverlapAbleSpaces.top.h - addCtrlSpace.h
        } else {
          locateAddCtrlSpace.y = addCtrlSpace.y
        }
        locateAddCtrlSpace.h = addCtrlSpace.h
      } else if ([VIEW_TYPE.TOP, VIEW_TYPE.BOTTOM].includes(viewSet.viewType)) {
        locateAddCtrlSpace.y = addTargetCell.y
        locateAddCtrlSpace.h = addTargetCell.h
      }
      locateAddCtrlSpace.x = addTargetCell.x
      locateAddCtrlSpace.w = addTargetCell.w
      break
    case PLANNING_TYPE.UNIT_SHELF:
    case PLANNING_TYPE.UNIT_INFILL:
      // ダミーは除外
      if ([OBJECT_DTL_TYPE.UNIT_INNER_SEPARATOR_DAMMY.code, OBJECT_DTL_TYPE.UNIT_CONST_.code].includes(addCtrlSpace.spaceObjectDtlType)) {
        break
      }
      // 上下に配置する棚orインフィル
      if (addCtrlSpace.h < addCtrlSpace.w && addCtrlSpace.h < addCtrlSpace.d) {
        if ([VIEW_TYPE.FRONT, VIEW_TYPE.BACK, VIEW_TYPE.RIGHT, VIEW_TYPE.LEFT].includes(viewSet.viewType)) {
          // セルからはみ出ていたらセル内に収まるように設定する
          if (addCtrlSpace.y <= addTargetCell.y) {
            locateAddCtrlSpace.y = addTargetCell.y
          } else if (addCtrlSpace.y + addCtrlSpace.h >= addTargetCell.y + addTargetCell.h) {
            locateAddCtrlSpace.y = addTargetCell.y + addTargetCell.h - addCtrlSpace.h
          } else {
            locateAddCtrlSpace.y = addCtrlSpace.y
          }
          locateAddCtrlSpace.h = addCtrlSpace.h
        } else if ([VIEW_TYPE.TOP, VIEW_TYPE.BOTTOM].includes(viewSet.viewType)) {
          locateAddCtrlSpace.y = addTargetCell.y
          locateAddCtrlSpace.h = addTargetCell.h
        }
        locateAddCtrlSpace.x = addTargetCell.x
        locateAddCtrlSpace.w = addTargetCell.w
      // 左右に配置する棚orインフィル
      } else if (addCtrlSpace.w < addCtrlSpace.h && addCtrlSpace.w < addCtrlSpace.d) {
        if ([VIEW_TYPE.FRONT, VIEW_TYPE.BACK, VIEW_TYPE.TOP, VIEW_TYPE.BOTTOM].includes(viewSet.viewType)) {
          // セルからはみ出ていたらセル内に収まるように設定する
          if (addCtrlSpace.x <= addTargetCell.x) {
            locateAddCtrlSpace.x = addTargetCell.x
          } else if (addCtrlSpace.x + addCtrlSpace.w >= addTargetCell.x + addTargetCell.w) {
            locateAddCtrlSpace.x = addTargetCell.x + addTargetCell.w - addCtrlSpace.w
          } else {
            locateAddCtrlSpace.x = addCtrlSpace.x - addCtrlSpace.w / 2
          }
          locateAddCtrlSpace.w = addCtrlSpace.w
        } else if ([VIEW_TYPE.RIGHT, VIEW_TYPE.LEFT].includes(viewSet.viewType)) {
          locateAddCtrlSpace.x = addTargetCell.x
          locateAddCtrlSpace.w = addTargetCell.w
        }
        locateAddCtrlSpace.y = addTargetCell.y
        locateAddCtrlSpace.h = addTargetCell.h
      // 前後に配置する棚orインフィル
      } else if (addCtrlSpace.d < addCtrlSpace.w && addCtrlSpace.d < addCtrlSpace.h) {
        if ([VIEW_TYPE.FRONT, VIEW_TYPE.BACK].includes(viewSet.viewType)) {
          locateAddCtrlSpace.x = addTargetCell.x
          locateAddCtrlSpace.w = addTargetCell.w
        } else if ([VIEW_TYPE.TOP, VIEW_TYPE.BOTTOM, VIEW_TYPE.RIGHT, VIEW_TYPE.LEFT].includes(viewSet.viewType)) {
          // セルからはみ出ていたらセル内に収まるように設定する
          if (addCtrlSpace.x <= addTargetCell.x) {
            locateAddCtrlSpace.x = addTargetCell.x
          } else if (addCtrlSpace.x + addCtrlSpace.w >= addTargetCell.x + addTargetCell.w) {
            locateAddCtrlSpace.x = addTargetCell.x + addTargetCell.w - addCtrlSpace.w
          } else {
            locateAddCtrlSpace.x = addCtrlSpace.x - addCtrlSpace.w / 2
          }
          locateAddCtrlSpace.w = addCtrlSpace.w
        }
        locateAddCtrlSpace.y = addTargetCell.y
        locateAddCtrlSpace.h = addTargetCell.h
      }
      break
    default:
      break
  }

  return locateAddCtrlSpace
}

/**
 * 上書き対象の空間を取得
 * @param addCtrlSpace ドラッグしてる空間（正面からの空間）
 * @param addTargetSpace 上書き対象の空間
 * @param globalSpaceMap グローバル空間マップ
 * @param viewSet ビューセット
 * @returns
 */
export const getTargetSwapSpaceLines = (addCtrlSpace, addTargetSpace, globalSpaceMap, viewSet) => {
  if (!addCtrlSpace || !addTargetSpace || addCtrlSpace.planningType !== addTargetSpace.planningType ||
      ![PLANNING_TYPE.UNIT_SHELF, PLANNING_TYPE.UNIT_DOOR, PLANNING_TYPE.UNIT_DRAWER, PLANNING_TYPE.COMBINE].includes(addCtrlSpace.planningType) ||
      ![PLANNING_TYPE.UNIT_SHELF, PLANNING_TYPE.UNIT_DOOR, PLANNING_TYPE.UNIT_DRAWER, PLANNING_TYPE.COMBINE].includes(addTargetSpace.planningType) ||
      !viewSet || !viewSet.viewType || !globalSpaceMap || !globalSpaceMap.get(viewSet.viewType)) {
    return {}
  }
  return globalSpaceMap.get(viewSet.viewType).find(space => space.id === addTargetSpace.id)
}

/**
 * 表面オプションの配置対象の空間を取得
 * @param addCtrlSpace ドラッグしてる空間（正面からの空間）
 * @param addTargetSpace 追加対象の空間
 * @param globalSpaceMap グローバル空間マップ
 * @param viewSet ビューセット
 * @returns
 */
export const getTargetFaceOpAddSpaceLines = (addCtrlSpace, addTargetSpace, globalSpaceMap, viewSet) => {
  if (!addCtrlSpace || !addTargetSpace ||
      addCtrlSpace.planningType !== PLANNING_TYPE.UNIT_FACE_OPTION || addTargetSpace.spaceObjectType !== OBJECT_TYPE.UNIT.code ||
      !viewSet || !viewSet.viewType || !globalSpaceMap || !globalSpaceMap.get(viewSet.viewType)) {
    return {}
  }
  return globalSpaceMap.get(viewSet.viewType).find(space => space.id === addTargetSpace.id)
}

/**
 * セルの上下左右に隣接する引き出しが重ねて配置することができる空間を取得
 * @param cell 対象のセル(グローバル空間)
 * @param globalSpaces グローバル空間
 * @returns 上下左右の空間をマップ
 */
export const getDrawerOverlapAbleSpacesOfCell = (cell, globalSpaces) => {
  let overlapAbleSpaces = { top: null, bottom: null, right: null, left: null }
  if (globalSpaces == null) return overlapAbleSpaces

  // セルの真ん中のx座標
  let cellHalfX = cell.x + cell.w / 2
  // セルの真ん中のy座標
  let cellHalfY = cell.y + cell.h / 2

  // セルの上に隣接しており、引き出しが重ねることができない空間
  let notTargetTopSpaces = globalSpaces.filter(space =>
    (space.planningType === PLANNING_TYPE.UNIT_DRAWER || space.planningType === PLANNING_TYPE.UNIT_DOOR) &&
    space.y === cell.y + cell.h &&
    space.x <= cellHalfX && cellHalfX <= space.x + space.w
  )
  // セルの上に隣接する空間
  let topSpaces = globalSpaces.filter(space =>
    space.planningType !== PLANNING_TYPE.ELEMENT &&
    space.y === cell.y + cell.h &&
    space.x <= cellHalfX && cellHalfX <= space.x + space.w
  )
  if (notTargetTopSpaces.length <= 0 && topSpaces.length > 0) {
    let topSpace = JSON.parse(JSON.stringify(topSpaces[0]))
    // 天板と側板のチリサイズを調整
    if (topSpace.spaceObjectDtlType === OBJECT_DTL_TYPE.PART_TOP_BOARD.code) {
      let topParentSpace = globalSpaces.find(s => s.id === topSpace.parentSpaceId)
      let offSet = Math.abs((topParentSpace.y + topParentSpace.h) - (topSpace.y + topSpace.h))
      // チリサイズを1ミリ以下に限定
      topSpace.h += offSet > 1.0 ? 0 : offSet
    }
    overlapAbleSpaces.top = topSpace
  }

  // セルの下に隣接しており、引き出しが重ねることができない空間
  let notTargetBottomSpaces = globalSpaces.filter(space =>
    (space.planningType === PLANNING_TYPE.UNIT_DRAWER || space.planningType === PLANNING_TYPE.UNIT_DOOR) &&
    space.y + space.h === cell.y &&
    space.x <= cellHalfX && cellHalfX <= space.x + space.w
  )
  // セルの下に隣接する空間
  let bottomSpaces = globalSpaces.filter(space =>
    space.planningType !== PLANNING_TYPE.ELEMENT &&
    space.y + space.h === cell.y &&
    space.x <= cellHalfX && cellHalfX <= space.x + space.w
  )
  if (notTargetBottomSpaces.length <= 0 && bottomSpaces.length > 0) {
    let bottomSpace = JSON.parse(JSON.stringify(bottomSpaces[0]))
    // 底板と側板のチリサイズを調整
    if (bottomSpace.spaceObjectDtlType === OBJECT_DTL_TYPE.PART_BOTTOM_BOARD.code) {
      let bottomParentSpace = globalSpaces.find(s => s.id === bottomSpace.parentSpaceId)
      let offSet = Math.abs(bottomParentSpace.y - bottomSpace.y)
      // チリサイズを1ミリ以下に限定
      bottomSpace.h += offSet > 1.0 ? 0 : offSet
    }
    overlapAbleSpaces.bottom = bottomSpace
  }

  // セルの右に隣接しており、引き出しが重ねることができない空間
  let notTargetRightSpaces = globalSpaces.filter(space =>
    (space.planningType === PLANNING_TYPE.UNIT_DRAWER || space.planningType === PLANNING_TYPE.UNIT_DOOR) &&
    space.x === cell.x + cell.w &&
    space.y <= cellHalfY && cellHalfY <= space.y + space.h
  )
  // セルの右に隣接する空間
  let rightSpaces = globalSpaces.filter(space =>
    space.planningType !== PLANNING_TYPE.ELEMENT &&
    space.x === cell.x + cell.w &&
    space.y <= cellHalfY && cellHalfY <= space.y + space.h
  )
  if (notTargetRightSpaces.length <= 0 && rightSpaces.length > 0) {
    overlapAbleSpaces.right = rightSpaces[0]
  }

  // セルの左に隣接しており、引き出しが重ねることができない空間
  let notTargetLeftSpaces = globalSpaces.filter(space =>
    (space.planningType === PLANNING_TYPE.UNIT_DRAWER || space.planningType === PLANNING_TYPE.UNIT_DOOR) &&
    space.x + space.w === cell.x &&
    space.y <= cellHalfY && cellHalfY <= space.y + space.h
  )
  // セルの左に隣接する空間
  let leftSpaces = globalSpaces.filter(space =>
    space.planningType !== PLANNING_TYPE.ELEMENT &&
    space.x + space.w === cell.x &&
    space.y <= cellHalfY && cellHalfY <= space.y + space.h
  )
  if (notTargetLeftSpaces.length <= 0 && leftSpaces.length > 0) {
    overlapAbleSpaces.left = leftSpaces[0]
  }

  return overlapAbleSpaces
}

/**
 * 棚に吸着している場合、編集している扉の真下のセルを取得する
 * @param snapPositions 吸着情報
 * @param editCtrlSpaces 編集操作用空間データ
 * @param fileData ファイルデータ
 * @param globalSpaceMap グローバル座標空間マップ
 * @returns セル
 */
export const getDestCellOfDoor = (snapPositions, editCtrlSpaces, fileData, globalSpaceMap) => {
  let destCell = null

  if (!snapPositions || !snapPositions.positionList ||
  !((snapPositions.positionList.x && snapPositions.positionList.x.length > 0 && snapPositions.positionList.x.find(space => space.tagSpace.planningType === PLANNING_TYPE.UNIT_SHELF)) ||
  (snapPositions.positionList.y && snapPositions.positionList.y.length > 0 && snapPositions.positionList.y.find(space => space.tagSpace.planningType === PLANNING_TYPE.UNIT_SHELF))) ||
  !editCtrlSpaces || editCtrlSpaces.length !== 1 || editCtrlSpaces[0].planningType !== PLANNING_TYPE.UNIT_DOOR) {
    return destCell
  }

  let positionList = snapPositions.positionList
  let door = editCtrlSpaces[0]

  // x軸に吸着している場合
  if (positionList.x && positionList.x.length > 0) {
    // 吸着先の空間の中から操作している扉に関連している棚を取得
    let targetSnapPosition = positionList.x.find(position => {
      let centerOfTagSpace = position.tagSpace.y + position.tagSpace.h / 2
      return door.y <= centerOfTagSpace && centerOfTagSpace <= door.y + door.h
    })
    if (targetSnapPosition == null) return destCell
    let shelf = targetSnapPosition.tagSpace

    // 棚に隣接しているセルの中から編集している引き出しに最も近いセルを取得する
    let nextCells = getNextCellsOfShelf(fileData, shelf, globalSpaceMap)
    let doorXCenter = door.x + door.w / 2
    let leftCell = nextCells.left
    let rightCell = nextCells.right
    destCell = Math.abs((leftCell.x + leftCell.w / 2) - doorXCenter) < Math.abs((rightCell.x + rightCell.w / 2) - doorXCenter) ? leftCell : rightCell
  // y軸に吸着している場合
  } else if (positionList.y && positionList.y.length > 0) {
    // 吸着先の空間の中から編集している扉に関連している棚を取得
    let targetSnapPosition = positionList.y.find(position => {
      let centerOfTagSpace = position.tagSpace.x + position.tagSpace.w / 2
      return door.x <= centerOfTagSpace && centerOfTagSpace <= door.x + door.w
    })
    if (targetSnapPosition == null) return destCell
    let shelf = targetSnapPosition.tagSpace

    // 棚に隣接しているセルの中から編集している引き出しに最も近いセルを取得する
    let nextCells = getNextCellsOfShelf(fileData, shelf, globalSpaceMap)
    let doorYCenter = door.y + door.h / 2
    let topCell = nextCells.top
    let bottomCell = nextCells.bottom
    destCell = Math.abs((topCell.y + topCell.h / 2) - doorYCenter) < Math.abs((bottomCell.y + bottomCell.h / 2) - doorYCenter) ? topCell : bottomCell
  }

  return destCell
}
/**
 * 指定した軸に対して、２つの空間が重なっているかを判定
 * @param editCtrlSpace 選択空間
 * @param targetSpace 対象空間
 * @param axisType 判定方向
 */
export const isOverlapSpaceToAxis = (editCtrlSpace, targetSpace, axisType) => {
  if (axisType === AXIS_TYPE.X) {
    return (targetSpace.x < editCtrlSpace.x + editCtrlSpace.w && targetSpace.x + targetSpace.w > editCtrlSpace.x) ||
    (targetSpace.x + targetSpace.w > editCtrlSpace.x && targetSpace.x < editCtrlSpace.x + editCtrlSpace.w)
  }
  if (axisType === AXIS_TYPE.Y) {
    return (targetSpace.y < editCtrlSpace.h + editCtrlSpace.h && targetSpace.y + targetSpace.h > editCtrlSpace.y) ||
    (targetSpace.y + targetSpace.h > editCtrlSpace.y && targetSpace.y < editCtrlSpace.y + editCtrlSpace.h)
  }
  return false
}

/**
 * 横ルール
 * 幅比率設定セットプラン作成条件を満たすか判定
 * @param editCtrlSpaces 選択空間
 */
export const isRuledSetByWidth = (editCtrlSpaces) => {
  editCtrlSpaces.sort((a, b) => a.x - b.x)
  // 一番高さが大きい空間
  let maxHSpace = editCtrlSpaces.sort((a, b) => b.h - a.h)[0]
  // 一番奥行が大きい空間
  let maxDSpace = editCtrlSpaces.sort((a, b) => b.d - a.d)[0]
  // サイズ条件を満たす空間に限定
  let targetSpaceList = editCtrlSpaces.filter(space =>
    maxHSpace.y <= space.y && maxHSpace.y + maxHSpace.h >= space.y + space.h &&
    maxDSpace.z <= space.z && maxDSpace.z + maxDSpace.d >= space.z + space.d
  )
  return targetSpaceList.length === editCtrlSpaces.length
}

/**
 * 縦ルール
 * 縦比率設定セットプラン作成条件を満たすか判定
 * @param editCtrlSpaces 選択空間
 */
export const isRuledSetByHeight = (editCtrlSpaces) => {
  editCtrlSpaces.sort((a, b) => a.y - b.y)
  // 一番幅が大きい空間
  let maxWSpace = editCtrlSpaces.sort((a, b) => b.w - a.w)[0]
  // 一番奥行が大きい空間
  let maxDSpace = editCtrlSpaces.sort((a, b) => b.d - a.d)[0]
  // サイズ条件を満たす空間orカウンターに限定
  let targetSpaceList = editCtrlSpaces.filter(space =>
    space.objDtlType === OBJECT_DTL_TYPE.UNIT_COUNTER ||
      (maxWSpace.x <= space.x && maxWSpace.x + maxWSpace.w >= space.x + space.w &&
        maxDSpace.z <= space.z && maxDSpace.z + maxDSpace.d >= space.z + space.d)
  )
  return targetSpaceList.length === editCtrlSpaces.length
}

/**
 * 奥行ルール
 * 奥行比率設定セットプラン作成条件を満たすか判定
 * @param editCtrlSpaces 選択空間
 */
export const isRuledSetByDepth = (editCtrlSpaces) => {
  editCtrlSpaces.sort((a, b) => a.z - b.z)
  // 一番幅が大きい空間
  let maxWSpace = editCtrlSpaces.sort((a, b) => b.w - a.w)[0]
  // 一番高さが大きい空間
  let maxHSpace = editCtrlSpaces.sort((a, b) => b.h - a.h)[0]
  // サイズ条件を満たす空間に限定
  let targetSpaceList = editCtrlSpaces.filter(space =>
    maxWSpace.x <= space.x && maxWSpace.x + maxWSpace.w >= space.x + space.w &&
    maxHSpace.y <= space.y && maxHSpace.y + maxHSpace.h >= space.y + space.h
  )
  return targetSpaceList.length === editCtrlSpaces.length
}

/**
 * サイズ取得
 */
export const getSize = (space, axisType) => {
  switch (axisType) {
    case AXIS_TYPE.X: return space.w
    case AXIS_TYPE.Y: return space.h
    case AXIS_TYPE.Z: return space.d
    default:
  }
  return null
}

/**
 * サイズ設定
 */
export const setSize = (space, axisType, size) => {
  switch (axisType) {
    case AXIS_TYPE.X:
      space.w = size
      break
    case AXIS_TYPE.Y:
      space.h = size
      break
    case AXIS_TYPE.Z:
      space.d = size
      break
    default:
  }
}

/**
 * 位置設定
 */
export const setOrigin = (space, axisType, position) => {
  switch (axisType) {
    case AXIS_TYPE.X:
      space.x = position
      break
    case AXIS_TYPE.Y:
      space.y = position
      break
    case AXIS_TYPE.Z:
      space.z = position
      break
    default:
  }
}

/**
 * ローカルストレージからファイルデータを取得
 */
export const getFileData = (fileDataId) => {
  return JSON.parse(localStorage.getItem(fileDataId))
}

/**
 * 画面表示用ガイド線データ生成
 * @param  {Object} data 変換元ガイド線データ
 * @param  {Object} viewType ビュー種別(VIEW_TYPE)
 * @param  {Number} sectionPosition 断面位置
 * @return {Object} 表示用ガイド線データ
 */
export const convertGuideLineDataForDisp = (data, viewType, sectionPosition) => {
  if (!Object.keys(data).length) return {}
  let line = {}
  switch (viewType) {
    case VIEW_TYPE.FRONT:
      if (data.z > sectionPosition.z) return line
      switch (data.axis) {
        case AXIS_TYPE.X:
          line = {
            x1: data.x,
            y1: data.y,
            x2: data.x + data.l,
            y2: data.y
          }
          break
        case AXIS_TYPE.Y:
          line = {
            x1: data.x,
            y1: data.y,
            x2: data.x,
            y2: data.y + data.l
          }
          break
      }
      break
    case VIEW_TYPE.RIGHT:
      if (data.x > sectionPosition.x) return line
      switch (data.axis) {
        case AXIS_TYPE.Y:
          line = {
            x1: data.z,
            y1: data.y,
            x2: data.z,
            y2: data.y + data.l
          }
          break
        case AXIS_TYPE.Z:
          line = {
            x1: data.z,
            x2: data.z + data.l,
            y1: data.y,
            y2: data.y
          }
          break
      }
      break
    case VIEW_TYPE.TOP:
      if (data.y > sectionPosition.y) return line
      switch (data.axis) {
        case AXIS_TYPE.X:
          line = {
            x1: data.x,
            x2: data.x + data.l,
            y1: data.z,
            y2: data.z
          }
          break
        case AXIS_TYPE.Z:
          line = {
            x1: data.x,
            x2: data.x,
            y1: data.z,
            y2: data.z + data.l
          }
          break
      }
      break
    default:
  }
  return line
}



// WEBPACK FOOTER //
// ./src/main/js/utils/Space.js